/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Feb 05 2025
 **/

import { create } from "zustand";

export type DefaultType = {
	id: string; // whiteboard, screenID, fileID, tusID
	type: "whiteboard" | "file" | "screen";
	isSharing: boolean;
	isExpanding?: boolean;
};

export type FileType = {
	uploaderID: string;
	fileInfo: SwaggerFileAPIModels.FileBoxResponse & {
		docImageId?: string;
		sceneLevel?: string;
		uploadType: "local" | "fileBox";
		spaceID?: string;
		spaceType?: number;
		isLoading?: boolean;
	};
} & DefaultType;

export type ScreenType = {
	uploaderID: string;
	isLoading?: boolean;
} & DefaultType;

export type SharedItem = DefaultType | FileType | ScreenType;

type FloatingListState = {
	sharedList: SharedItem[];
};

type FloatingListAction = {
	addToSharedList: (item: SharedItem) => void;
	removeFromSharedList: (id: string) => void;
	clearSharedList: () => void;
	updateIsSharing: (id: string, isSharing: boolean) => void;
	updateLoadedFile: (tusId: string, newItem: FileType) => void;
	updateScreenLoading: (id: string) => void;
	updateIsExpanding: (id: string, isExpanding: boolean) => void;
};

type FloatingListStoreProps = FloatingListState & FloatingListAction;

const DefaultFloatingListState: FloatingListState = {
	sharedList: [{ id: "whiteboard", isSharing: false, type: "whiteboard" }]
};

const useFloatingListStore = create<FloatingListStoreProps>((set) => ({
	...DefaultFloatingListState,
	addToSharedList: (item) =>
		set((state) => {
			if (state.sharedList.some(({ id }) => id === item.id)) {
				return state;
			}
			return {
				sharedList: [...state.sharedList, item]
			};
		}),
	removeFromSharedList: (id) =>
		set((state) => ({
			sharedList: state.sharedList.filter((item) => item.id !== id)
		})),
	clearSharedList: () =>
		set(() => ({
			sharedList: [...DefaultFloatingListState.sharedList]
		})),
	updateIsSharing: (id, isSharing) =>
		set((state) => ({
			sharedList: state.sharedList.map((item) => {
				if (item.id === id) {
					return { ...item, isSharing };
				}
				return item;
			})
		})),
	updateLoadedFile: (tusId, newItem) => {
		set((state) => {
			return {
				sharedList: state.sharedList.map((item) => (item.type === "file" && item.id === tusId ? newItem : item))
			};
		});
	},
	updateScreenLoading: (id) => {
		set((state) => {
			return {
				sharedList: state.sharedList.map((item) =>
					item.type === "screen" && item.id === id ? { ...item, isLoading: false } : item
				)
			};
		});
	},
	updateIsExpanding: (id, isExpanding) =>
		set((state) => ({
			sharedList: state.sharedList.map((item) => {
				if (item.id === id) {
					return { ...item, isExpanding };
				}
				return item;
			})
		}))
}));

export default useFloatingListStore;
